'use strict';

const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';
const SELECTED_NOTIFICATIONS_GROUP_LOCAL_STORAGE_KEY = 'Selected-Notifications-Group';

export default angular
  .module('main.notifications.groups.edit.dataProvider', [])
  .factory('EditNotificationGroupDataProvider', function(
    $q,
    $log,
    $rootScope,
    $state,
    $filter,
    utils,
    UsersService,
    Resource,
    NotificationService,
  ) {
    class EditNotificationGroupDataProvider {

      // #region Resources

      /**
       * Current organization resource
       * @type {Resource}
       */
      currentOrganizationResource = new Resource(() => {
        return utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
      });

      /**
       * Selected notification group (with users).
       * If the group stored on list page selection matches the groupId from the URL path,
       * reuse it without an API call. Otherwise (e.g. navigated from the organization page)
       * fetch it by the URL groupId.
       * @type {Resource}
       */
      selectedGroupResource = new Resource(() => {
        const groupId = parseInt($state.params.groupId);
        const storedGroup = utils.getStorageItem(SELECTED_NOTIFICATIONS_GROUP_LOCAL_STORAGE_KEY);

        // Use the group cached on list-page selection only for the very first load
        // (fast render, no API call). On any subsequent re-init (e.g. after adding or
        // removing a user) always fetch fresh so the users list reflects the change.
        if (!this.#groupLoaded && (!groupId || (storedGroup && storedGroup.groupId === groupId))) {
          this.#groupLoaded = true;
          return storedGroup;
        }
        this.#groupLoaded = true;

        return NotificationService.getNotificationGroups(
          this.currentOrganizationResource.value.id,
          groupId || storedGroup?.groupId
        )
          .then(data => (data.groups && data.groups[0]) || null);
      }, [
        this.currentOrganizationResource,
      ]);

      /**
       * Users of the notification group
       * @type {Resource}
       */
      groupUsersResource = new Resource(() => {
        return this.selectedGroupResource.value?.users || [];
      }, [
        this.selectedGroupResource,
      ]);

      /**
       * Notification settings of the notification group
       * @type {Resource}
       */
      notificationSettingsResource = new Resource(() => {
        return NotificationService.getNotificationGroupSettings(this.selectedGroupResource.value.organizationId)
          .then(data => (data && data.notifications || []));
      }, [
        this.selectedGroupResource,
      ]);

      /**
       * Current (active) tab
       * @type {'users'}
       */
      get activeTab() {
        return this.#_activeTab;
      }

      /**
       * Previous tab
       * @return {'users'|undefined}
       */
      get previousTab() {
        return this.#_previousTab;
      }

      set activeTab(val) {
        this.#_visitedTabs[val] = true;
        if (this.#_activeTab !== val) {
          this.#_previousTab = this.#_activeTab;
        }
        this.#_activeTab = val;
      }

      /**
       * Hash of visited tabs
       */
      get visitedTabs() {
        return this.#_visitedTabs;
      }

      // #endregion

      // #region Private fields

      #_activeTab;

      #_previousTab;

      #_visitedTabs = {};

      /**
       * Whether the group has been loaded at least once.
       * After the first load we always fetch fresh (never reuse the cached list-page group).
       */
      #groupLoaded = false;

      // #endregion

      // #region Public methods

      /**
       * Universal handle function for Cancel or Back button
       */
      goBack() {
        $state.go('main.notifications.groups.list');
      }

      /**
       * Destroy current Data Provider.
       * Obviously, you can not use this object after destruction.
       */
      destroy() {
        // destroy every Resource and Resource Group in current object
        for (let [key, value] of Object.entries(this)) {
          if (value && value.isResource) {
            value.destroy();
            delete this[key];
          }
        }
      }

      /**
       * Search users across entire cloud
       * @param {string} [email] Email address to search by.
       * @param {string} [phone] Phone number to search by.
       * @returns {Promise}
       */
      searchUsersGlobal(email, phone) {
        return UsersService.searchUsers(email, phone)
          .then(data => data);
      }

      /**
       * Add a user to the current notification group
       * @param {number} userId User ID to add to the group
       * @returns {Promise}
       */
      addNotificationUser(userId) {
        return NotificationService.updateNotificationUsers(this.currentOrganizationResource.value.id, {
          users: [{
            userId: userId,
            groupId: this.selectedGroupResource.value.groupId,
          }]
        })
          .then(data => {
            return this.selectedGroupResource.init(true)
              .then(() => this.groupUsersResource.init(true))
              .then(() => data);
          });
      }

      /**
       * Remove a user from the current notification group
       * @param {number} userId User ID to remove from the group
       * @param {boolean} allGroups Remove the user from all notification groups
       * @returns {Promise}
       */
      removeNotificationUser(userId, allGroups) {
        return NotificationService.updateNotificationUsers(this.currentOrganizationResource.value.id, {
          users: [{
            userId: userId,
            groupId: allGroups ? void 0 : this.selectedGroupResource.value.groupId,
            delete: true,
          }]
        })
          .then(data => {
            return this.selectedGroupResource.init(true)
              .then(() => this.groupUsersResource.init(true))
              .then(() => data);
          });
      }

      /**
       * Enable or disable a bot notification for the current group.
       * @param {Object} notification Flattened notification (must carry its app `bundle`)
       * @param {boolean} enabled Whether the notification should be enabled for this group
       * @returns {Promise}
       */
      updateNotificationSetting(notification, enabled) {
        return NotificationService.updateNotificationGroupSettings(this.selectedGroupResource.value.organizationId, {
          groups: [{
            groupId: this.selectedGroupResource.value.groupId,
            notificationId: notification.notificationId,
            delete: !enabled,
          }]
        })
          .then(data => {
            return this.notificationSettingsResource.init(true)
              .then(() => data);
          });
      }

      // #endregion

    }

    return EditNotificationGroupDataProvider;
  })
  .name;
